<template>
    <div class="emr-lookup-participant-modal">

        <!-- emr lookup modal activation button -->
        <b-btn class="btn-secondary ml-2"
               size="sm"
               @click="emrLookupShow($event)">
            {{ $i18n.gt('LABEL_LOOKUP') }}
        </b-btn>

        <!-- modal -->
        <b-modal hide-footer
                 size="xl"
                 :id="modal_id"
                 :title="$i18n.gt('LABEL_EMR_LOOKUP')">

            <data-table ref="dataTable"
                        :show-filters="true"
                        :show-pager="true"
                        :show-details="false"
                        :get-items="getParticipants"
                        :filters="filters"
                        :fields="fields">

                <!-- custom template for filters -->
                <template v-slot:filters>
                    <b-row cols="5">
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_FIRST_NAME')">
                                <b-form-input v-model="filters.firstname.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_LAST_NAME')">
                                <b-form-input v-model="filters.lastname.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_GENDER')">
                                <b-form-select :options="genderOptions"
                                               v-model="filters.gender.value"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_DOB')">
                                <b-form-input type="date"
                                              v-model="filters.dob.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group horizontal
                                          :label="$i18n.gt('LABEL_PHONE')">
                                <b-form-input v-model="filters.telecom.value"
                                              @input="filtersChanged()"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </template>

                <!-- template for custom columns -->
                <template v-slot:customField="slotProps">
                    <div v-if="slotProps.row.field.key === 'gender'">
                        <template v-if="slotProps.row.item['gender'] === 'F'">
                            {{ $i18n.gt('FIELD_PARTICIPANT_GENDER_FEMALE') }}
                        </template>
                        <template v-else-if="slotProps.row.item['gender'] === 'M'">
                            {{ $i18n.gt('FIELD_PARTICIPANT_GENDER_MALE') }}
                        </template>
                        <template v-else-if="slotProps.row.item['gender'] === 'UNKNOWN'">
                            {{ $i18n.gt('FIELD_PARTICIPANT_GENDER_UNKNOWN') }}
                        </template>
                        <template v-else>
                            {{ slotProps.row.item['gender'] }}
                        </template>
                    </div>
                    <div v-else-if="slotProps.row.field.key === 'dob'">
                        <timestamp :date="slotProps.row.field.value"
                                   :includeYear="true"/>
                    </div>
                    <div v-else-if="slotProps.row.field.key === 'actions'">
                        <b-btn class="btn btn-ghost-blue btn-sm btn-icon mr-2"
                               @click="selectRecord(slotProps.row.item, false)">
                            {{ $i18n.gt('LABEL_EMR_COPY_IDENTIFIER') }}
                        </b-btn>
                        <b-btn class="btn btn-ghost-blue btn-sm btn-icon mr-2"
                               @click="selectRecord(slotProps.row.item, true)">
                            {{ $i18n.gt('LABEL_EMR_COPY_ALL') }}
                        </b-btn>
                        <b-btn v-if="canDelete"
                               class="btn btn-sm btn-icon mr-2"
                               variant="danger"
                               @click="deleteRecord(slotProps.row.item)">
                            {{ $i18n.gt('LABEL_EMR_DELETE') }}
                        </b-btn>
                    </div>
                </template>
            </data-table>
        </b-modal>
    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'

export default Vue.extend({
    name: 'emr-lookup-participant-modal',
    props: {
        fieldModel: {type: Object, default: null},
        form: {type: Object, default: null},
        participant: {type: Number, default: null},
    },
    data() {
        return {
            modal_id: "emrLookupParticipant",
            filters: {
                firstname: {field: 'firstname', value: null},
                lastname: {field: 'lastname', value: null},
                gender: {field: 'gender', value: null},
                telecom: {field: 'telecom', value: null},
                dob: {field: 'dob', value: null},
            },
            genderOptions: [
                {value: 'Male', text: this.$i18n.gt('FIELD_PARTICIPANT_GENDER_MALE')},
                {value: 'Female', text: this.$i18n.gt('FIELD_PARTICIPANT_GENDER_FEMALE')},
                {value: 'Unknown', text: this.$i18n.gt('FIELD_PARTICIPANT_GENDER_UNKNOWN')},
            ],
            canDelete: false,
        }
    },
    computed: {
        fields() {
            return [
                {key: 'firstname', sortable: true, label: this.$i18n.gt('LABEL_FIRST_NAME')},
                {key: 'lastname', sortable: true, label: this.$i18n.gt('LABEL_LAST_NAME')},
                {key: 'gender', sortable: true, label: this.$i18n.gt('LABEL_GENDER')},
                {key: 'dob', sortable: true, label: this.$i18n.gt('LABEL_DOB')},
                {key: 'phone_primary', sortable: true, label: this.$i18n.gt('LABEL_PHONE')},
                {key: 'actions', sortable: false, custom: true, label: ''},
            ]
        }
    },
    methods: {
        emrLookupShow(e) {
            if (e.shiftKey && this.$user.roleIs('ADMIN')) {
                this.canDelete = true
            }
            if (this.fieldModel && this.fieldModel.fields && this.form) {
                if (this.form['firstname'] && this.form['firstname'].$model) {
                    this.filters.firstname.value = this.form['firstname'].$model
                }
                if (this.form['lastname'] && this.form['lastname'].$model) {
                    this.filters.lastname.value = this.form['lastname'].$model
                }
                if (this.form['gender'] && this.form['gender'].$model) {
                    const gender = this.form['gender'].$model
                    if (gender === 'F') {
                        this.filters.gender.value = 'Female'
                    } else if (gender === 'M') {
                        this.filters.gender.value = 'Male'
                    } else if (gender === 'UNKNOWN') {
                        this.filters.gender.value = 'Unknown'
                    } else if (gender) {
                        console.log('EmrLookupModal.emrLookupShow  unknown gender=', gender)
                    }
                }
                if (this.form['dob'] && this.form['dob'].$model) {
                    this.filters.dob.value = this.form['dob'].$model
                }
                if (this.form['phone_primary'] && this.form['phone_primary'].$model) {
                    this.filters.telecom.value = this.form['phone_primary'].$model
                }
            }
            this.$bvModal.show('emrLookupParticipant')
        },
        getParticipants(listParams) {
            let filters = this.filters
            filters.type = {field: 'type', value: 'participant'}
            return this.$api.emrLookup(this, this.filters, listParams, {participant: this.participant})
        },
        filtersChanged() {
            this.$refs.dataTable.debouncedRefresh()
        },
        selectRecord(item, copy) {
            if (this.form['emr_id']) {
                if (this.form && item['emr_id']) {
                    this.form['emr_id'].$model = item['emr_id']
                }
                if (copy) {
                    if (this.form['firstname']) {
                        this.form['firstname'].$model = item.firstname
                    }
                    if (this.form['lastname']) {
                        this.form['lastname'].$model = item.lastname
                    }
                    if (this.form['gender']) {
                        this.form['gender'].$model = item.gender
                    }
                    if (this.form['dob']) {
                        this.form['dob'].$model = item.dob
                    }
                    if (this.form['phone_primary']) {
                        this.form['phone_primary'].$model = item.phone_primary
                    }
                }
            }
            this.$root.$emit('bv::hide::modal', this.modal_id)
        },
        async deleteRecord(item) {
            if (await this.$bvModal.msgBoxConfirm(this.$i18n.gt('LABEL_EMR_CONFIRM_DELETE_PARTICIPANT'))) {
                try {
                    let result = await this.$api.emrDelete(this, 'participant', item['emr_id'], this.$user.practitionerPracticeId())
                    if (result) {
                        if (result.issue && Array.isArray(result.issue) && result.issue.length) {
                            let issue = result.issue[0]
                            this.$utils.handleError(null, 'Unable to delete EMR record')
                        } else {
                            this.$refs.dataTable.refresh()
                        }
                    }
                } catch (e) {
                    this.$utils.handleError(e, 'Unable to delete EMR record.')
                }
            }
        }
    },
})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

.emr-lookup-participant-modal {
    display: inline-block;
}

</style>
